import { useAtom, useAtomValue } from 'jotai'
import invariant from 'tiny-invariant'

import { Button, Flex } from '@sequencehq/core-components'
import { applyNewDiscountToPhases, priceModelAdapter } from '@sequencehq/utils'
import {
  DiscountEditorMode,
  useDiscountEditor,
  DiscountEditorProvider,
  discountEditorCubeAdapter,
  DiscountEditorForm,
  useDiscountEditorContext
} from 'Cube/view/common/drawers/discountEditor'
import { scheduleCurrencyAtom } from '../../../atoms/reviewPricing.atoms'
import {
  Phase,
  pricingDetailsAtom
} from '../../../atoms/reviewPricing.primitives.atoms'
import {
  getDiscountScopeAvailability,
  getPricesForPhase,
  updatePhase
} from '../../../utils/phases'
import {
  pricingUtilsDiscountAdapter,
  pricingUtilsPhaseAdapter
} from '../reviewPricingDrawers.adapters'
import { match } from 'ts-pattern'
import { StepDrawer } from '../../../../components/StepDrawer'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

type Props = {
  phaseId: string
  discountId: string | undefined
  onDismiss: () => void
}

export const DiscountEditorDrawer = ({
  phaseId,
  discountId,
  onDismiss
}: Props) => {
  const currency = useAtomValue(scheduleCurrencyAtom)
  const [pricingDetails, setPricingDetails] = useAtom(pricingDetailsAtom)
  const { useStackedDiscounts } = useFeatureFlags()

  const phase = pricingDetails.phases.find(phase => phase.id === phaseId)
  invariant(phase, 'phase is undefined')

  const existingDiscount = phase.discounts.find(
    discount => discount.id === discountId
  )

  const allPricesForPhase = getPricesForPhase({
    phase,
    prices: pricingDetails.prices
  })

  const { availablePriceIds, isApplyToAllAvailable } =
    getDiscountScopeAvailability({
      phase,
      prices: pricingDetails.prices,
      discountId: existingDiscount?.id
    })

  const defaultApplyTo = phase.priceIds.length ? 'SPECIFIC' : 'ALL'

  const discountEditorDomain = useDiscountEditor({
    mode: discountId ? DiscountEditorMode.EDIT : DiscountEditorMode.CREATE,
    onSave: discount => {
      const newDiscount = discountEditorCubeAdapter.out(discount)

      /**
       * Only the phase being edited is passed, so the update stops there.
       */
      const updates = applyNewDiscountToPhases({
        newDiscount,
        phaseId,
        phases: [
          pricingUtilsPhaseAdapter.in({ phase, prices: pricingDetails.prices })
        ],
        allowStackedDiscounts: useStackedDiscounts
      })

      const updatedPhases: Phase[] = updatePhase({
        phases: pricingDetails.phases,
        phaseId,
        update: phaseToUpdate => ({
          ...phaseToUpdate,
          discounts: (
            updates.updatedPhases[phaseId]?.discountIds ??
            phaseToUpdate.discounts.map(({ id }) => id)
          ).map(discountId => {
            const updatedDiscount = updates.updatedDiscounts[discountId]
            const existingDiscount = phaseToUpdate.discounts.find(
              discount => discount.id === discountId
            )

            if (!updatedDiscount) {
              invariant(existingDiscount, 'no existing discount')
              return existingDiscount
            }

            return pricingUtilsDiscountAdapter.out(
              updatedDiscount,
              existingDiscount?.boundingBoxIndex ?? -1
            )
          })
        })
      })

      setPricingDetails({ phases: updatedPhases })
      onDismiss()
    },
    onClose: () => {
      onDismiss()
    },
    allPrices: allPricesForPhase
      .map(price =>
        priceModelAdapter.in({
          ...price,
          productId: '__IGNORE__'
        })
      )
      .map(({ productId, ...price }) => price),
    availablePrices: availablePriceIds,
    existingDiscount: existingDiscount
      ? discountEditorCubeAdapter.in(
          pricingUtilsDiscountAdapter.in(existingDiscount),
          { currency }
        )
      : undefined,
    defaultApplyTo,
    currency,
    applyToAllAvailable: isApplyToAllAvailable
  })

  return (
    <DiscountEditorProvider value={discountEditorDomain}>
      <DiscountEditorDrawerContent />
    </DiscountEditorProvider>
  )
}

const getDrawerTitle = (mode: DiscountEditorMode) => {
  return match(mode)
    .with(DiscountEditorMode.CREATE, () => 'Add discount')
    .with(DiscountEditorMode.VIEW, () => 'View discount')
    .with(DiscountEditorMode.EDIT, () => 'Edit discount')
    .exhaustive()
}

const getSaveText = (mode: DiscountEditorMode) => {
  return match(mode)
    .with(DiscountEditorMode.CREATE, () => 'Add discount')
    .with(DiscountEditorMode.VIEW, () => 'Update discount')
    .with(DiscountEditorMode.EDIT, () => 'Update discount')
    .exhaustive()
}

const DiscountEditorDrawerContent = () => {
  const discountEditorContext = useDiscountEditorContext()
  return (
    <StepDrawer
      data-testid="reviewPricing.drawers.discountEditor"
      title={getDrawerTitle(discountEditorContext.editor.mode)}
      onClose={discountEditorContext.functions.closeEditor}
      footer={
        <>
          <Button
            variant="secondary"
            data-testid="reviewPricing.drawers.discountEditor.cancel"
            onClick={discountEditorContext.functions.closeEditor}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="reviewPricing.drawers.discountEditor.save"
            onClick={discountEditorContext.functions.saveDiscount}
          >
            {getSaveText(discountEditorContext.editor.mode)}
          </Button>
        </>
      }
    >
      <Flex gap="16px" flexDir="column">
        <DiscountEditorForm padding="0px" />
      </Flex>
    </StepDrawer>
  )
}
